import {
  JSNS,
  type JsonObject,
  type ActivityInfo,
  type Comment,
  type DiffChunk,
  type DocumentActionGrant,
  type DocumentRevision,
  type SavedDocument,
} from '@zimbra-webif/types';
import type { ZimbraClient } from '../client';

/** SaveDocument content source — exactly one of these is used. */
export interface SaveDocumentSource {
  /** id returned by the upload servlet (see uploadFileUrl) */
  uploadId?: string;
  /** pull content from a message attachment */
  from?: { msgId: string; part: string };
  /** inline content body */
  content?: string;
  /** copy from another doc/revision */
  copyFrom?: { id: string; ver: number };
}

export interface SaveDocumentOptions extends SaveDocumentSource {
  /** existing doc id (omit for a new doc) */
  id?: string;
  /** last-known version (required when adding a revision; optimistic concurrency) */
  ver?: number;
  name?: string;
  /** content type */
  contentType?: string;
  /** destination folder id */
  folderId?: string;
  /** description */
  desc?: string;
  descEnabled?: boolean;
  /** flags (e.g. "t" = note) */
  flags?: string;
}

/** Document workflow action options. */
export interface DocumentActionOptions {
  folderId?: string;
  name?: string;
  /** grantee/owner zimbra id (for grant ops) */
  zimbraId?: string;
  /** read-share-by-link grant */
  grant?: DocumentActionGrant;
  /** flags */
  flags?: string;
  /** tags */
  tags?: string;
  /** constraint */
  tcon?: string;
}

/** Selector for GetItem / GetDocumentShareURL. */
export interface ItemSelector {
  id?: string;
  /** containing folder id */
  folder?: string;
  name?: string;
  path?: string;
}

/**
 * Briefcase / Documents (urn:zimbraMail).
 *
 * Briefcase items are versioned `document`-view MailItems. Document metadata,
 * revisions, comments, activity and per-item custom metadata are SOAP; the
 * actual file BYTES move out-of-band over REST/servlet endpoints, so content
 * upload/download here are BFF stubs (URL/param builders) — they do NOT perform
 * HTTP. See docs/03-api.md and docs/features/sharing-docs-sync.md §E.
 */
export class BriefcaseApi {
  constructor(private readonly client: ZimbraClient) {}

  /* ---------------- Content I/O — REST/servlet BFF stubs ---------------- */

  /**
   * BFF endpoint, see docs/03-api.md — the FileUploadServlet endpoint a raw
   * upload PUTs/POSTs to; the returned upload-id is then passed to saveDocument
   * as `uploadId`. Returns the URL only; the BFF performs the multipart POST.
   */
  uploadFileUrl(): string {
    // BFF endpoint, see docs/03-api.md
    return '/service/upload?fmt=raw';
  }

  /**
   * BFF endpoint, see docs/03-api.md — builds the REST download URL for a
   * briefcase document. Either by item id (`?id=`) or by user + filename path.
   * Returns the URL only; the BFF streams the bytes.
   */
  documentDownloadUrl(item: { id?: string; user?: string; filename?: string }): string {
    // BFF endpoint, see docs/03-api.md
    if (item.user && item.filename) {
      return `/home/${encodeURIComponent(item.user)}/Briefcase/${encodeURIComponent(item.filename)}`;
    }
    const user = item.user ?? '~';
    if (item.id) {
      return `/home/${encodeURIComponent(user)}/?id=${encodeURIComponent(item.id)}`;
    }
    return `/home/${encodeURIComponent(user)}/Briefcase/`;
  }

  /* ---------------- Document SOAP commands ---------------- */

  /**
   * SaveDocument (urn:zimbraMail) — create a document or add a revision.
   * New doc: omit `id`+`ver`. Revision: pass `id` + last-known `ver`
   * (optimistic concurrency — fails on version mismatch).
   */
  async saveDocument(opts: SaveDocumentOptions): Promise<SavedDocument> {
    const doc: JsonObject = {};
    if (opts.id !== undefined) doc.id = opts.id;
    if (opts.ver !== undefined) doc.ver = opts.ver;
    if (opts.name !== undefined) doc.name = opts.name;
    if (opts.contentType !== undefined) doc.ct = opts.contentType;
    if (opts.folderId !== undefined) doc.l = opts.folderId;
    if (opts.desc !== undefined) doc.desc = opts.desc;
    if (opts.descEnabled !== undefined) doc.descEnabled = opts.descEnabled;
    if (opts.flags !== undefined) doc.f = opts.flags;

    if (opts.uploadId !== undefined) {
      doc.upload = { id: opts.uploadId };
    } else if (opts.from) {
      doc.m = { id: opts.from.msgId, part: opts.from.part };
    } else if (opts.content !== undefined) {
      doc.content = { _content: opts.content };
    } else if (opts.copyFrom) {
      doc.doc = { id: opts.copyFrom.id, ver: opts.copyFrom.ver };
    }

    const res = await this.client.call<{ doc?: SavedDocument | SavedDocument[] }>(
      JSNS.mail,
      'SaveDocument',
      { doc },
    );
    const d = res.doc;
    return (Array.isArray(d) ? d[0] : d) ?? ({} as SavedDocument);
  }

  /**
   * GetItem (urn:zimbraMail) — fetch an item's metadata. The response `item` is
   * polymorphic (`<doc>` / `<folder>` / `<m>` / …); callers discriminate on the
   * returned element key.
   */
  async getItem(sel: ItemSelector): Promise<JsonObject> {
    const item: JsonObject = {};
    if (sel.id !== undefined) item.id = sel.id;
    if (sel.folder !== undefined) item.l = sel.folder;
    if (sel.name !== undefined) item.name = sel.name;
    if (sel.path !== undefined) item.path = sel.path;
    return this.client.call<JsonObject>(JSNS.mail, 'GetItem', { item });
  }

  /**
   * DocumentAction (urn:zimbraMail) — rename/move/trash/grant/lock/watch a doc.
   * `op` is the action verb (move, trash, delete, rename, grant, !grant, watch,
   * !watch, lock, !lock, …).
   */
  async documentAction(op: string, id: string, opts?: DocumentActionOptions): Promise<JsonObject> {
    const action: JsonObject = { op, id };
    if (opts?.folderId !== undefined) action.l = opts.folderId;
    if (opts?.name !== undefined) action.name = opts.name;
    if (opts?.zimbraId !== undefined) action.zid = opts.zimbraId;
    if (opts?.flags !== undefined) action.f = opts.flags;
    if (opts?.tags !== undefined) action.t = opts.tags;
    if (opts?.tcon !== undefined) action.tcon = opts.tcon;
    if (opts?.grant !== undefined) action.grant = opts.grant;
    return this.client.call<JsonObject>(JSNS.mail, 'DocumentAction', { action });
  }

  /** ListDocumentRevisions (urn:zimbraMail) — revision history of a document. */
  async listDocumentRevisions(
    id: string,
    opts?: { ver?: number; count?: number },
  ): Promise<DocumentRevision[]> {
    const doc: JsonObject = { id };
    if (opts?.ver !== undefined) doc.ver = opts.ver;
    if (opts?.count !== undefined) doc.count = opts.count;
    const res = await this.client.call<{ doc?: DocumentRevision[] }>(
      JSNS.mail,
      'ListDocumentRevisions',
      { doc },
    );
    return res.doc ?? [];
  }

  /** PurgeRevision (urn:zimbraMail) — drop an old revision (optionally older too). */
  async purgeRevision(id: string, ver: number, includeOlder?: boolean): Promise<void> {
    const revision: JsonObject = { id, ver };
    if (includeOlder !== undefined) revision.includeOlderRevisions = includeOlder;
    await this.client.call(JSNS.mail, 'PurgeRevision', { revision });
  }

  /** DiffDocument (urn:zimbraMail) — diff two revisions of a document. */
  async diffDocument(id: string, v1: number, v2: number): Promise<DiffChunk[]> {
    const res = await this.client.call<{ chunk?: DiffChunk[] }>(JSNS.mail, 'DiffDocument', {
      doc: { id, v1, v2 },
    });
    return res.chunk ?? [];
  }

  /** GetDocumentShareURL (urn:zimbraMail) — a public/share URL for a document. */
  async getDocumentShareURL(sel: { id?: string; path?: string }): Promise<string> {
    const item: JsonObject = {};
    if (sel.id !== undefined) item.id = sel.id;
    if (sel.path !== undefined) item.path = sel.path;
    const res = await this.client.call<{ url?: string | { _content?: string } }>(
      JSNS.mail,
      'GetDocumentShareURL',
      { item },
    );
    if (typeof res.url === 'string') return res.url;
    return res.url?._content ?? '';
  }

  /** AddComment (urn:zimbraMail) — add a comment to an item; returns the comment id. */
  async addComment(itemId: string, text: string): Promise<string> {
    const res = await this.client.call<{ comment?: { id?: string } }>(JSNS.mail, 'AddComment', {
      comment: { parentId: itemId, text },
    });
    return res.comment?.id ?? '';
  }

  /** GetComments (urn:zimbraMail) — list comments on an item. */
  async getComments(itemId: string): Promise<Comment[]> {
    const res = await this.client.call<{ comment?: Comment[] }>(JSNS.mail, 'GetComments', {
      comment: { parentId: itemId },
    });
    return res.comment ?? [];
  }

  /** GetActivityStream (urn:zimbraMail) — activity history for an item. */
  async getActivityStream(
    itemId: string,
    opts?: { offset?: number; limit?: number; ops?: string; accountIds?: string },
  ): Promise<ActivityInfo[]> {
    const params: JsonObject = { id: itemId };
    if (opts?.offset !== undefined) params.offset = opts.offset;
    if (opts?.limit !== undefined) params.limit = opts.limit;
    if (opts?.ops !== undefined || opts?.accountIds !== undefined) {
      const filter: JsonObject = {};
      if (opts.ops !== undefined) filter.op = opts.ops;
      if (opts.accountIds !== undefined) filter.account = opts.accountIds;
      params.filter = filter;
    }
    const res = await this.client.call<{ a?: ActivityInfo[] }>(
      JSNS.mail,
      'GetActivityStream',
      params,
    );
    return res.a ?? [];
  }

  /** GetCustomMetadata (urn:zimbraMail) — a custom per-item metadata section. */
  async getCustomMetadata(itemId: string, section: string): Promise<Record<string, string>> {
    const res = await this.client.call<{
      meta?: { a?: Array<{ n?: string; _content?: string }> };
    }>(JSNS.mail, 'GetCustomMetadata', {
      id: itemId,
      meta: { section },
    });
    const out: Record<string, string> = {};
    for (const a of res.meta?.a ?? []) {
      if (a.n !== undefined) out[a.n] = a._content ?? '';
    }
    return out;
  }

  /** SetCustomMetadata (urn:zimbraMail) — set a custom per-item metadata section. */
  async setCustomMetadata(
    itemId: string,
    section: string,
    attrs: Record<string, string>,
  ): Promise<void> {
    const a = Object.entries(attrs).map(([n, value]) => ({ n, _content: value }));
    await this.client.call(JSNS.mail, 'SetCustomMetadata', {
      id: itemId,
      meta: { section, a },
    });
  }

  /**
   * FileSharedWithMe (urn:zimbraMail) — records, in the grantee's mailbox, that a
   * single Briefcase file was shared with them (the file-level analogue of a
   * share notification). Internal: called as part of an accept-file-share flow,
   * not a user-facing query.
   */
  async fileSharedWithMe(params: {
    action: string;
    fileName: string;
    ownerFileId: string;
    fileUUID: string;
    fileOwnerName: string;
    rights: string;
    contentType: string;
    size: number;
    ownerAccountId: string;
    date: number;
  }): Promise<JsonObject> {
    return this.client.call<JsonObject>(JSNS.mail, 'FileSharedWithMe', { ...params });
  }
}
